import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, CheckCircle, XCircle, Clock, Eye, FileText, Download, Send } from 'lucide-react';
import api from '../../services/api';

function AdminInternships() {
  const navigate = useNavigate();
  const [internships, setInternships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterReview, setFilterReview] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInternship, setSelectedInternship] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    paymentLink: '',
    mentorMessage: '',
    projectSteps: ''
  });

  useEffect(() => {
    fetchInternships();
  }, []);

  useEffect(() => {
    fetchInternships();
  }, [filterStatus, filterReview]);

  const fetchInternships = async () => {
    try {
      let url = '/admin/internships?';
      if (filterStatus) url += `status=${filterStatus}&`;
      if (filterReview) url += `reviewStatus=${filterReview}&`;
      if (searchTerm) url += `search=${searchTerm}`;

      const res = await api.get(url);
      setInternships(res.data);
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const viewDetails = async (internshipId) => {
    try {
      const res = await api.get(`/admin/internships/${internshipId}`);
      setSelectedInternship(res.data);
      setShowDetailModal(true);
    } catch (error) {
      alert('Error loading internship details');
    }
  };

  const handleSendPaymentLink = async (e) => {
    e.preventDefault();
    
    if (!paymentForm.paymentLink) {
      alert('Please enter a payment link');
      return;
    }

    try {
      await api.post(`/admin/internships/${selectedInternship._id}/send-payment-link`, paymentForm);
      alert('✅ Payment link sent successfully! Student will receive email with all details.');
      setShowPaymentModal(false);
      setShowDetailModal(false);
      setPaymentForm({ paymentLink: '', mentorMessage: '', projectSteps: '' });
      fetchInternships();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to send payment link');
    }
  };

  const handleSearch = () => {
    fetchInternships();
  };

  const updateReviewStatus = async (status, notes = '') => {
    try {
      await api.patch(`/admin/internships/${selectedInternship._id}/review`, {
        reviewStatus: status,
        reviewNotes: notes
      });
      alert(`Review status updated to ${status}`);
      setShowDetailModal(false);
      fetchInternships();
    } catch (error) {
      alert('Failed to update review status');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'pending-review': { color: 'warning', text: 'Pending Review' },
      'payment-pending': { color: 'info', text: 'Payment Pending' },
      'active': { color: 'success', text: 'Active' },
      'completed': { color: 'success', text: 'Completed' },
      'discontinued': { color: 'danger', text: 'Discontinued' }
    };
    const badge = badges[status] || { color: 'secondary', text: status };
    return <span className={`badge bg-${badge.color}`}>{badge.text}</span>;
  };

  const getReviewBadge = (reviewStatus) => {
    const badges = {
      'pending': { color: 'warning', text: 'Pending' },
      'approved': { color: 'success', text: 'Approved' },
      'rejected': { color: 'danger', text: 'Rejected' }
    };
    const badge = badges[reviewStatus] || { color: 'secondary', text: reviewStatus };
    return <span className={`badge bg-${badge.color}`}>{badge.text}</span>;
  };

  return (
    <div className="container-fluid mt-4">
      <div className="row mb-3">
        <div className="col">
          <h2>Internship Management</h2>
          <p className="text-muted">Review applications, verify documents, and send payment links</p>
        </div>
      </div>

      {/* Filters */}
      <div className="row mb-3">
        <div className="col-md-2">
          <select 
            className="form-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="pending-review">Pending Review</option>
            <option value="payment-pending">Payment Pending</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="discontinued">Discontinued</option>
          </select>
        </div>
        <div className="col-md-2">
          <select 
            className="form-select"
            value={filterReview}
            onChange={(e) => setFilterReview(e.target.value)}
          >
            <option value="">All Reviews</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div className="col-md-5">
          <div className="input-group">
            <input 
              type="text" 
              className="form-control"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button className="btn btn-primary" onClick={handleSearch}>
              Search
            </button>
          </div>
        </div>
        <div className="col-md-3">
          <button className="btn btn-secondary" onClick={() => {
            setFilterStatus('');
            setFilterReview('');
            setSearchTerm('');
            fetchInternships();
          }}>
            Clear Filters
          </button>
          <span className="ms-3 text-muted">
            Total: {internships.length}
          </span>
        </div>
      </div>

      {loading ? (
        <div className="text-center"><div className="spinner-border"></div></div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead>
              <tr>
                <th>Student Name</th>
                <th>Email</th>
                <th>Program</th>
                <th>Project</th>
                <th>Type</th>
                <th>Price</th>
                <th>Review Status</th>
                <th>Status</th>
                <th>Applied Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {internships.map((internship) => (
                <tr key={internship._id}>
                  <td><strong>{internship.legalFullName}</strong></td>
                  <td>{internship.officialEmail}</td>
                  <td>{internship.program?.title}</td>
                  <td>{internship.assignedProject?.name}</td>
                  <td>
                    <span className={`badge ${internship.internshipType === 'with-mentor' ? 'bg-primary' : 'bg-secondary'}`}>
                      {internship.internshipType === 'with-mentor' ? 'With Mentor' : 'Without Mentor'}
                    </span>
                  </td>
                  <td>₹{internship.price}</td>
                  <td>{getReviewBadge(internship.reviewStatus)}</td>
                  <td>{getStatusBadge(internship.status)}</td>
                  <td>{new Date(internship.createdAt).toLocaleDateString()}</td>
                  <td>
                    <button 
                      className="btn btn-sm btn-info"
                      onClick={() => viewDetails(internship._id)}
                    >
                      <Eye className="w-4 h-4" /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedInternship && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Internship Application Details</h5>
                <button className="btn-close" onClick={() => setShowDetailModal(false)}></button>
              </div>
              <div className="modal-body" style={{maxHeight: '80vh', overflowY: 'auto'}}>
                
                {/* Status and Review */}
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-body">
                        <h6>Current Status</h6>
                        <div className="mb-2">
                          <strong>Review:</strong> {getReviewBadge(selectedInternship.reviewStatus)}
                        </div>
                        <div>
                          <strong>Status:</strong> {getStatusBadge(selectedInternship.status)}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-body">
                        <h6>Quick Actions</h6>
                        {selectedInternship.reviewStatus === 'pending' && (
                          <div className="d-flex gap-2">
                            <button 
                              className="btn btn-sm btn-success"
                              onClick={() => updateReviewStatus('approved')}
                            >
                              <CheckCircle className="w-4 h-4" /> Approve
                            </button>
                            <button 
                              className="btn btn-sm btn-danger"
                              onClick={() => {
                                const notes = prompt('Reason for rejection:');
                                if (notes) updateReviewStatus('rejected', notes);
                              }}
                            >
                              <XCircle className="w-4 h-4" /> Reject
                            </button>
                          </div>
                        )}
                        {(selectedInternship.reviewStatus === 'approved' || selectedInternship.status === 'payment-pending') && !selectedInternship.paymentLinkSentAt && (
                          <button 
                            className="btn btn-sm btn-primary"
                            onClick={() => setShowPaymentModal(true)}
                          >
                            <Send className="w-4 h-4" /> Send Payment Link
                          </button>
                        )}
                        {selectedInternship.paymentLinkSentAt && (
                          <div className="alert alert-info mb-0">
                            Payment link sent on {new Date(selectedInternship.paymentLinkSentAt).toLocaleString()}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Personal Information */}
                <div className="card mb-3">
                  <div className="card-header bg-primary text-white">
                    <h6 className="mb-0">Personal Information</h6>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <p><strong>Legal Name:</strong> {selectedInternship.legalFullName}</p>
                        <p><strong>DOB:</strong> {new Date(selectedInternship.dateOfBirth).toLocaleDateString()}</p>
                        <p><strong>Phone:</strong> {selectedInternship.phone}</p>
                        <p><strong>Email:</strong> {selectedInternship.officialEmail}</p>
                      </div>
                      <div className="col-md-6">
                        <p><strong>Emergency Contact:</strong> {selectedInternship.emergencyContact.name}</p>
                        <p><strong>Emergency Phone:</strong> {selectedInternship.emergencyContact.phone}</p>
                        <p><strong>Category:</strong> <span className="badge bg-success">{selectedInternship.user?.category}</span></p>
                        <p><strong>Skill Tier:</strong> {selectedInternship.user?.skillTier}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div className="card mb-3">
                  <div className="card-header">
                    <h6 className="mb-0">Address Details</h6>
                  </div>
                  <div className="card-body">
                    <p><strong>Current Address:</strong><br/>{selectedInternship.currentAddress}</p>
                    <p><strong>Permanent Address:</strong><br/>{selectedInternship.permanentAddress || 'Same as current'}</p>
                  </div>
                </div>

                {/* Educational Information */}
                <div className="card mb-3">
                  <div className="card-header">
                    <h6 className="mb-0">Educational Information</h6>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <p><strong>Qualification:</strong> {selectedInternship.highestQualification}</p>
                        <p><strong>College:</strong> {selectedInternship.collegeName}</p>
                      </div>
                      <div className="col-md-6">
                        <p><strong>Graduation Year:</strong> {selectedInternship.graduationYear}</p>
                        <p><strong>Student ID:</strong> {selectedInternship.studentId || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Internship Details */}
                <div className="card mb-3">
                  <div className="card-header bg-success text-white">
                    <h6 className="mb-0">Internship Details</h6>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <p><strong>Type:</strong> <span className="badge bg-primary">
                          {selectedInternship.internshipType === 'with-mentor' ? 'With Mentorship' : 'Without Mentorship'}
                        </span></p>
                        <p><strong>Price:</strong> ₹{selectedInternship.price}</p>
                        <p><strong>Program:</strong> {selectedInternship.program?.title}</p>
                      </div>
                      <div className="col-md-6">
                        <p><strong>Project:</strong> {selectedInternship.assignedProject?.name}</p>
                        <p><strong>Difficulty:</strong> {selectedInternship.assignedProject?.difficulty}</p>
                        <p><strong>Duration:</strong> {selectedInternship.assignedProject?.duration}</p>
                      </div>
                    </div>
                    <div className="alert alert-info">
                      <strong>Project Description:</strong><br/>
                      {selectedInternship.assignedProject?.description}
                    </div>
                  </div>
                </div>

                {/* Uploaded Documents */}
                <div className="card mb-3">
                  <div className="card-header bg-warning">
                    <h6 className="mb-0"><FileText className="w-4 h-4 inline" /> Uploaded Documents</h6>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      {Object.entries(selectedInternship.documents).map(([key, value]) => (
                        value && (
                          <div key={key} className="col-md-6 mb-2">
                            <strong>{key.replace(/([A-Z])/g, ' $1').toUpperCase()}:</strong>
                            <a 
                              href={`https://api.internstacks.com${value}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline-primary ms-2"
                            >
                              <Download className="w-3 h-3" /> View/Download
                            </a>
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                </div>

                {/* Review Notes */}
                {selectedInternship.reviewNotes && (
                  <div className="alert alert-warning">
                    <strong>Review Notes:</strong><br/>
                    {selectedInternship.reviewNotes}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowDetailModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Link Modal */}
      {showPaymentModal && selectedInternship && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.6)'}}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">Send Payment Link & Project Details</h5>
                <button className="btn-close btn-close-white" onClick={() => setShowPaymentModal(false)}></button>
              </div>
              <form onSubmit={handleSendPaymentLink}>
                <div className="modal-body">
                  <div className="alert alert-info">
                    <strong>Student:</strong> {selectedInternship.legalFullName}<br/>
                    <strong>Email:</strong> {selectedInternship.officialEmail}<br/>
                    <strong>Project:</strong> {selectedInternship.assignedProject?.name}<br/>
                    <strong>Amount:</strong> ₹{selectedInternship.price}
                  </div>

                  <div className="mb-3">
                    <label className="form-label"><strong>Payment Link *</strong></label>
                    <input
                      type="url"
                      className="form-control"
                      placeholder="https://razorpay.com/payment/..."
                      value={paymentForm.paymentLink}
                      onChange={(e) => setPaymentForm({...paymentForm, paymentLink: e.target.value})}
                      required
                    />
                    <small className="text-muted">Razorpay, PayPal, Stripe, or any payment link</small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label"><strong>Message from Mentor</strong></label>
                    <textarea
                      className="form-control"
                      rows={3}
                      placeholder="Welcome message for the student..."
                      value={paymentForm.mentorMessage}
                      onChange={(e) => setPaymentForm({...paymentForm, mentorMessage: e.target.value})}
                    />
                    <small className="text-muted">This will be highlighted in the email</small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label"><strong>Project Steps/Instructions (Optional)</strong></label>
                    <textarea
                      className="form-control"
                      rows={5}
                      placeholder="Step 1: Setup development environment
Step 2: Clone repository
Step 3: Install dependencies..."
                      value={paymentForm.projectSteps}
                      onChange={(e) => setPaymentForm({...paymentForm, projectSteps: e.target.value})}
                    />
                    <small className="text-muted">Provide initial steps or setup instructions</small>
                  </div>

                  <div className="alert alert-warning">
                    <strong>⚠️ Important:</strong> Once sent, the student will receive:
                    <ul className="mb-0 mt-2">
                      <li>Payment link</li>
                      <li>Project details and description</li>
                      <li>Mentor message (highlighted)</li>
                      <li>Project steps (if provided)</li>
                      <li>Important notice about completion</li>
                    </ul>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowPaymentModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    <Send className="w-4 h-4" /> Send Email with Payment Link
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminInternships;
